import type { Vessel } from './types';

export type VesselIconKind =
  | 'tanker'
  | 'cargo'
  | 'container'
  | 'passenger'
  | 'fishing'
  | 'tug'
  | 'pleasure'
  | 'military'
  | 'unknown';

export type VesselClass = 'commercial' | 'fishing' | 'pleasure' | 'military';

export interface RichVessel extends Vessel {
  iconKind: VesselIconKind;
  vesselClass: VesselClass;
}

export function classifyVesselIconKind(v: Pick<Vessel, 'vesselType' | 'name'>): VesselIconKind {
  const type = (v.vesselType ?? '').toLowerCase();
  const name = (v.name ?? '').toLowerCase();
  if (/tanker|oil|chemical|lng|lpg|gas_carrier/.test(type)) return 'tanker';
  if (/container/.test(type)) return 'container';
  if (/bulk|cargo|general_cargo|roro|vehicle/.test(type)) return 'cargo';
  if (/passenger|cruise|ferry|ro_pax/.test(type)) return 'passenger';
  if (/fishing|trawler/.test(type) || /fish|trawl/.test(name)) return 'fishing';
  if (/tug|pilot|dredger|supply|offshore/.test(type)) return 'tug';
  if (/yacht|sailing|pleasure|recreational/.test(type)) return 'pleasure';
  if (/military|naval|warship|patrol|coast.?guard/.test(type) || /navy|coast guard/.test(name)) return 'military';
  return 'unknown';
}

function vesselClassFromIcon(iconKind: VesselIconKind): VesselClass {
  if (iconKind === 'fishing') return 'fishing';
  if (iconKind === 'pleasure') return 'pleasure';
  if (iconKind === 'military') return 'military';
  return 'commercial';
}

export function enrichVessel(v: Vessel): RichVessel {
  const iconKind = classifyVesselIconKind(v);
  return { ...v, iconKind, vesselClass: vesselClassFromIcon(iconKind) };
}

export function enrichVessels(list: Vessel[]): RichVessel[] {
  return list.map(enrichVessel);
}

export const VESSEL_ICON_LABELS: Record<VesselIconKind, string> = {
  tanker: 'Tanker',
  cargo: 'Cargo',
  container: 'Container ship',
  passenger: 'Passenger / cruise',
  fishing: 'Fishing',
  military: 'Military',
  tug: 'Tug / service',
  pleasure: 'Pleasure craft',
  unknown: 'Unknown vessel',
};
